import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareFromSquare } from '@fortawesome/pro-light-svg-icons';
import CustomerCareDTable from '../../../../../Shared/CustomerCareDTable';
import NewModal from '../../../../../Shared/NewModal';
import ImageCarousel from '../../../../../Operations/components/ImageCarousel';
import NoTabDataCard from '../../../../../Hubs/HubDetails/tabs/components/NoTabDataCard';
import { RootState } from '../../../../../../store';
import { getMedicationEventImages } from '../../../../../../store/hubOwner/actionCreators';
import { MedEventImageCapture } from '../../../../../../generated';
import { parseDateToLocal } from '../../../../../../shared/utils';
import { darkPurple, dimGrey } from '../../../../../../mainTheme';

interface ScheduleEventImagesProps {
  customerId: number;
  event: { id: string };
}

/** One table row per dispense attempt, oldest first so "Attempt 1" is the first try. */
interface CaptureRow extends MedEventImageCapture {
  id: string;
  attempt: number;
}

/**
 * "Medication images" tab of the schedule event popup (OI-1368).
 * Lists the seam-detection captures the hub stored for this med event, one
 * row per dispense attempt, and opens the images of an attempt in a carousel
 * modal (same viewer as the hub's Seam Detection Images tab).
 * Always re-fetches on mount: the presigned URLs are short-lived.
 */
const ScheduleEventImages: React.FC<ScheduleEventImagesProps> = ({
  customerId,
  event,
}) => {
  const dispatch = useDispatch();

  const medEventImages = useSelector(
    (state: RootState) => state.hubOwner.medEventImages,
  );

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [selectedCapture, setSelectedCapture] = useState<CaptureRow | null>(
    null,
  );

  useEffect(() => {
    let ignore = false; // Guard against setState after unmount / event change
    const fetchImages = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        await dispatch(getMedicationEventImages(Number(customerId), event.id));
      } catch {
        // Thunk already toasted; keep a local flag so the tab shows an error
        // state rather than the misleading "No data" card.
        if (!ignore) setHasError(true);
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };
    fetchImages();
    return () => {
      ignore = true;
    };
  }, [dispatch, customerId, event.id]);

  // Only render data that belongs to this exact owner + med event
  const isCurrent =
    medEventImages?.ownerId === Number(customerId) &&
    medEventImages?.medEventId === event.id;

  const rows: CaptureRow[] = isCurrent
    ? [...medEventImages.images.captures]
        .sort((a, b) => a.uploaded_at.localeCompare(b.uploaded_at))
        .map((capture, idx) => ({
          ...capture,
          id: capture.sentry_event_id,
          attempt: idx + 1,
        }))
    : [];

  const columns: GridColDef[] = [
    {
      field: 'attempt',
      headerName: 'Dispense attempt',
      flex: 1.2,
      renderCell: (params: GridRenderCellParams<CaptureRow>) =>
        `Attempt ${params.row.attempt}`,
    },
    {
      field: 'uploaded_at',
      headerName: 'Captured at',
      flex: 1.4,
      renderCell: (params: GridRenderCellParams<CaptureRow>) =>
        params.value
          ? parseDateToLocal(params.value, 'yyyy-MM-dd HH:mm:ss')
          : 'N/A',
    },
    {
      field: 'imageCount',
      headerName: 'Images',
      flex: 0.6,
      sortable: false,
      valueGetter: (params) => params.row.images.length,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams<CaptureRow>) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
            border: '1px solid var(--Outline-O5---Separator, #D4D4D9)',
            borderRadius: '8px',
            padding: { xs: '2px', sm: '4px' },
            height: { xs: '32px', sm: '36px' },
          }}
        >
          <Tooltip title="View images">
            <IconButton
              aria-label={`View images for attempt ${params.row.attempt}`}
              onClick={() => setSelectedCapture(params.row)}
            >
              <FontAwesomeIcon
                icon={faShareFromSquare}
                style={{ fontSize: '16px', color: dimGrey }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (hasError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight={300}
        role="alert"
      >
        <Typography
          sx={{
            color: darkPurple,
            fontSize: '18px',
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: '0.2px',
            mb: 1,
          }}
        >
          Couldn&apos;t load medication images
        </Typography>
        <Typography sx={{ color: dimGrey, fontSize: '14px', fontWeight: 500 }}>
          Close and reopen this event to try again
        </Typography>
      </Box>
    );
  }

  // The tab only exists when the API said images exist, so a store that holds
  // another event's images (a slower earlier fetch landed after ours) means
  // "still loading", never "No data".
  if (isLoading || !isCurrent) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={300}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: 320,
        overflowY: 'auto',
      }}
    >
      {rows.length ? (
        <CustomerCareDTable
          rows={rows}
          columns={columns}
          pageSize={5}
          defaultSortField="uploaded_at"
          defaultSortDirection="asc"
          hideViewAllButton
          idField="id"
        />
      ) : (
        <NoTabDataCard />
      )}

      {selectedCapture && (
        <NewModal
          showCloseX
          title="Medication images"
          onClose={() => setSelectedCapture(null)}
          maxWidth="sm"
        >
          <Typography sx={{ mb: 1, color: dimGrey, fontSize: '14px' }}>
            {`Attempt ${selectedCapture.attempt} · ${parseDateToLocal(
              selectedCapture.uploaded_at,
              'yyyy-MM-dd HH:mm:ss',
            )}`}
          </Typography>
          <ImageCarousel
            images={selectedCapture.images.map((img) => img.presigned_url)}
          />
        </NewModal>
      )}
    </Box>
  );
};

export default ScheduleEventImages;
